import { LanguageProvider } from './i18n/Language';
import { englishPath } from './i18n/paths';
import React, { Suspense, lazy, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { Navigate } from './i18n/links';
import { Toaster } from "./components/ui/sonner";
import Header from "./components/Header";
import Footer from "./components/Footer";
import StickyActions from "./components/StickyActions";
import { ContentLoader } from "./components/ContentLoader";
import SitemapRedirect from "./pages/SitemapRedirect";
import RobotsRedirect from "./pages/RobotsRedirect";
import SEOSchema from "./components/SEOSchema";

// Route-based code splitting to shrink the initial JS bundle.
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const LocationPage = lazy(() => import("./pages/LocationPage"));
const RoutePage = lazy(() => import("./pages/RoutePage"));
const VehiclePage = lazy(() => import("./pages/VehiclePage"));
const BlogList = lazy(() => import("./pages/BlogList"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const PolicyPage = lazy(() => import("./pages/PolicyPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const ScrollOnNavigate = () => {
  const { pathname: fullPathname, hash } = useLocation();
  const pathname = englishPath(fullPathname);
  const navigationType = useNavigationType();

  useEffect(() => {
    // Leave initial loads, browser history and in-page anchors to the browser.
    if (navigationType !== "POP" && !hash) window.scrollTo(0, 0);
  }, [pathname, hash, navigationType]);

  return null;
};

const Layout = ({ children }) => (
  <>
    <SEOSchema />
    <Header />
    <main>
      <Suspense fallback={<ContentLoader />}>{children}</Suspense>
    </main>
    <Footer />
    <StickyActions />
  </>
);

// The build renders the same route tree with StaticRouter; the browser keeps
// BrowserRouter so initial HTML and interactive content have identical markup.
const LocalizedRoutes = ({children}) => {
  const location=useLocation();
  const pathname=englishPath(location.pathname);
  return <Routes location={{...location,pathname: /^\/hi\/admin(?:\/|$)/.test(location.pathname) ? location.pathname : pathname}}>{children}</Routes>;
};
function App({ Router = BrowserRouter, routerProps = {} } = {}) {
  return (
    <Router {...routerProps}><LanguageProvider><div className="App">
        <ScrollOnNavigate />
        <LocalizedRoutes>
          {/* SEO Routes */}
          <Route path="/sitemap.xml" element={<SitemapRedirect />} />
          <Route path="/robots.txt" element={<RobotsRedirect />} />

          <Route path="/taxi-service-satna" element={<Navigate to="/taxi-service/satna" replace />} />
          <Route path="/taxi-service-rewa" element={<Navigate to="/taxi-service/rewa" replace />} />
          <Route path="/taxi-service-panna" element={<Navigate to="/taxi-service/panna" replace />} />
          <Route path="/taxi-service-khajuraho" element={<Navigate to="/taxi-service/khajuraho" replace />} />
          <Route path="/taxi-service-chhatarpur" element={<Navigate to="/taxi-service/chhatarpur" replace />} />
          <Route path="/taxi-service-karwi" element={<Navigate to="/taxi-service/karwi" replace />} />
          <Route path="/taxi-service-chitrakoot" element={<Navigate to="/taxi-service/chitrakoot" replace />} />
          <Route path="/taxi-service-chitrakoot-dham" element={<Navigate to="/taxi-service/chitrakoot-dham" replace />} />
          <Route path="/taxi-service-manikpur" element={<Navigate to="/taxi-service/manikpur" replace />} />
          <Route path="/taxi-service-mauganj" element={<Navigate to="/taxi-service/mauganj" replace />} />
          <Route path="/taxi-service-maihar" element={<Navigate to="/taxi-service/maihar" replace />} />
          <Route path="/taxi-service-katni" element={<Navigate to="/taxi-service/katni" replace />} />
          <Route path="/taxi-service-umaria" element={<Navigate to="/taxi-service/umaria" replace />} />
          <Route path="/taxi-service-bandhavgarh" element={<Navigate to="/taxi-service/bandhavgarh" replace />} />
          <Route path="/taxi-service-jabalpur" element={<Navigate to="/taxi-service/jabalpur" replace />} />
          <Route path="/taxi-service-shahdol" element={<Navigate to="/taxi-service/shahdol" replace />} />
          <Route path="/taxi-service-sagar" element={<Navigate to="/taxi-service/sagar" replace />} />
          <Route path="/taxi-service-damoh" element={<Navigate to="/taxi-service/damoh" replace />} />
          <Route path="/taxi-service-indore" element={<Navigate to="/taxi-service/indore" replace />} />
          <Route path="/taxi-service-bhopal" element={<Navigate to="/taxi-service/bhopal" replace />} />
          <Route path="/taxi-service-gwalior" element={<Navigate to="/taxi-service/gwalior" replace />} />
          <Route path="/taxi-service-prayagraj" element={<Navigate to="/taxi-service/prayagraj" replace />} />
          <Route path="/taxi-service-varanasi" element={<Navigate to="/taxi-service/varanasi" replace />} />
          <Route path="/taxi-service-lucknow" element={<Navigate to="/taxi-service/lucknow" replace />} />
          <Route path="/taxi-service-kanpur" element={<Navigate to="/taxi-service/kanpur" replace />} />
          <Route path="/taxi-service-agra" element={<Navigate to="/taxi-service/agra" replace />} />
          <Route path="/taxi-service/allahabad" element={<Navigate to="/taxi-service/prayagraj" replace />} />
          <Route path="/taxi-service-allahabad" element={<Navigate to="/taxi-service/prayagraj" replace />} />
          <Route path="/index.html" element={<Navigate to="/" replace />} />

          {/* Booking policies share the operator-approved content. */}
          <Route path="/terms" element={<Layout><PolicyPage kind="/terms" /></Layout>} />
          <Route path="/cancellation-policy" element={<Layout><PolicyPage kind="/cancellation-policy" /></Layout>} />
          <Route path="/refund-policy" element={<Layout><PolicyPage kind="/refund-policy" /></Layout>} />
          {/* Public Routes */}
          <Route path="/" element={<Layout><Home /></Layout>} />
          <Route path="/about" element={<Layout><About /></Layout>} />
          <Route path="/contact" element={<Layout><Contact /></Layout>} />
          <Route path="/taxi-service/:slug" element={<Layout><LocationPage /></Layout>} />
          <Route path="/route/:slug" element={<Layout><RoutePage /></Layout>} />
          <Route path="/vehicle/:slug" element={<Layout><VehiclePage /></Layout>} />
          <Route path="/blog" element={<Layout><BlogList /></Layout>} />
          <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />

          {/* Admin Routes */}
          <Route
            path="/admin/login"
            element={
              <Suspense fallback={<ContentLoader />}>
                <AdminLogin />
              </Suspense>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <Suspense fallback={<ContentLoader />}>
                <AdminDashboard />
              </Suspense>
            }
          />

          {/* 404 - catch all */}
          <Route path="*" element={<Layout><NotFound /></Layout>} />
        </LocalizedRoutes>
        <Toaster position="top-right" richColors />
    </div></LanguageProvider></Router>
  );
}

export default App;
